import type { FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'
import { hand } from '@/lib/fonts'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { SectionLabel } from '@/components/kit/section-label'
import { manifesto, manifestoHighlights } from '@/constants/home'

type Segment = {
    text: string
    marked: boolean
}

/* Split the manifesto into plain runs and highlighted phrases, in reading order. */
const segments: Array<Segment> = (() => {
    const result: Array<Segment> = []
    let rest = manifesto

    manifestoHighlights.forEach(phrase => {
        const at = rest.indexOf(phrase)

        if (at === -1) return

        if (at > 0) result.push({ text: rest.slice(0, at), marked: false })
        result.push({ text: phrase, marked: true })
        rest = rest.slice(at + phrase.length)
    })

    if (rest) result.push({ text: rest, marked: false })

    return result
})()

export const Manifesto: FC = () => {
    const sectionRef = useRef<HTMLElement>(null)
    const reduced = useReducedMotion()

    useScrollFrame(() => {
        const section = sectionRef.current

        if (!section) return

        const rect = section.getBoundingClientRect()
        const viewport = window.innerHeight
        const progress = Math.min(1, Math.max(0, (viewport * 0.8 - rect.top) / (rect.height * 0.75)))
        const words = section.querySelectorAll<HTMLElement>('[data-word]')
        const lit = Math.round(progress * words.length)

        words.forEach((word, index) => {
            const on = index < lit ? 'true' : 'false'

            if (word.dataset.lit !== on) word.dataset.lit = on
        })

        section.querySelectorAll<HTMLElement>('[data-marker]').forEach(marker => {
            const last = Number(marker.dataset.marker)
            const on = last < lit ? 'true' : 'false'

            if (marker.dataset.on !== on) marker.dataset.on = on
        })
    }, !reduced)

    let wordIndex = 0

    const renderWords = (text: string): ReactNode => {
        return text.split(/(\s+)/).map((part, index) => {
            if (!part.trim()) return <Fragment key={index}>{part}</Fragment>

            const order = wordIndex++

            return (
                <span
                    key={index}
                    data-word={order}
                    data-lit={reduced ? 'true' : 'false'}
                    className='wall-word'
                >
                    {part}
                </span>
            )
        })
    }

    return (
        <section
            ref={sectionRef}
            id='manifesto'
            aria-labelledby='manifesto-title'
            className='relative py-24 md:py-36'
        >
            <div className='mx-auto max-w-[1200px] px-5 md:px-10'>
                <div className='flex items-center justify-between gap-4'>
                    <SectionLabel index='01'>The editorial</SectionLabel>
                    <p className={cn(hand.className, 'hidden rotate-[-4deg] text-2xl text-ember md:block')}>
                        read it slowly, it lights up
                    </p>
                </div>

                <h2
                    id='manifesto-title'
                    className='sr-only'
                >
                    What we believe
                </h2>

                <p className='mt-10 font-heading text-[clamp(2.1rem,5.4vw,4.9rem)] font-bold leading-[1.04] tracking-[-0.04em]'>
                    {segments.map((segment, index) => {
                        if (!segment.marked) return <Fragment key={index}>{renderWords(segment.text)}</Fragment>

                        const content = renderWords(segment.text)

                        return (
                            <span
                                key={index}
                                data-marker={wordIndex - 1}
                                data-on={reduced ? 'true' : 'false'}
                                className='marker'
                            >
                                {content}
                            </span>
                        )
                    })}
                </p>

                <div className='mt-12 flex flex-wrap items-center gap-4 border-t border-ink/15 pt-6'>
                    <span className='stamp -rotate-3 text-ember'>Signed, the studio</span>
                    <p className='font-mono text-xs text-stone'>Updraft Studio, London. Est. 2019. Opinions our own.</p>
                </div>
            </div>
        </section>
    )
}
